import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import SaveButton from "../../../components/Buttons/SaveButton";
import DateControl from "../../../components/Fields/DateControl";
import TextboxControl from "../../../components/Fields/TextboxControl";
import TextboxField from "../../../components/Fields/TextboxField";
import { FormConfig } from "../../../components/Fields/Types";
import useValidation from "../../../components/Hooks/ValidationHook/useValidation";
import Popup from "../../../components/Popups/Popup";
import Translate from "../../../components/Translate";
import { useNotifications } from "../../../contexts/notificationContext";
import { useTranslations } from "../../../contexts/translationContext";
import ItemModel from "../../../models/ItemModel";
import ItemReferenceModel from "../../../models/ItemReferenceModel";
import DataService from "../../../services/data.service";
import { Result } from "../../../types/Result";

interface ItemReferencesUpdateModel {
    itemId: number
    itemNr: string
    customerSKU?: string
    licensorSKU?: string
    itemReferences: ItemReferenceModel[]
}

interface ItemRevisionPopupProps {
    show: boolean
    onHide: () => void
    item: ItemModel
}

const ChangeItemNrPopup: React.FC<ItemRevisionPopupProps> = ({ show, onHide, item }) => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const { handleResult, handleException } = useNotifications();

    const [formData, setFormData] = useState<ItemReferencesUpdateModel>({
        itemId: item.id,
        itemNr: item.itemNr,
        customerSKU: item.customerSKU,
        licensorSKU: item.licensorSKU,
        itemReferences: []
    });

    useEffect(() => {
        DataService.get<ItemReferenceModel[]>('api/v1/ItemReferences/ByItem/' + item.id)
            .then(response => {
                formData.itemReferences = response;
                setFormData({...formData})
            })
    }, [item]);

    function submit() {
        if (formData) {
            DataService
                .update(`api/v1/ItemReferences`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true) {
                        onHide();
                    }
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'itemNr', label: 'Item nr', validationRules: [{ type: 'isRequired' }, { type: 'isMaxLength', maxLength: 50 }] },
            { name: 'customerSKU', label: 'Customer SKU', validationRules: [{ type: 'isMaxLength', maxLength: 50 }] },
            { name: 'licensorSKU', label: 'LicensorSKU', validationRules: [{ type: 'isMaxLength', maxLength: 50 }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    if (formData.itemReferences) {
        formData.itemReferences.forEach((itemReference, index) => {
            formConfig.fields.push({ name: `itemReferences.${index}.referenceCode`, label: itemReference.externalSystemName, validationRules: [{ type: 'isMaxLength', maxLength: 50 }] });
            formConfig.fields.push({ name: `itemReferences.${index}.exportedOn`, label: 'Exported on' });
        })
    }

    const { getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    return (
        <Popup show={show} onHide={onHide} size={'lg'} title={'Change ItemNr'} >
            <Popup.Body>
                <TextboxField {...getFieldProps('itemNr')} />
                {item.itemType.hasCustomerSKU && < TextboxField {...getFieldProps('customerSKU')} />}
                {item.itemType.hasLicensorSKU && < TextboxField {...getFieldProps('licensorSKU')} />}
                {formData.itemReferences && formData.itemReferences.length > 0 && (
                    <>
                        <h4><b><Translate>External references</Translate></b></h4>
                        <table>
                            <tr>
                                <th className='col-md-2'><Translate>System</Translate></th>
                                <th className='col-md-6'><Translate>Reference code</Translate></th>
                                <th className='col-md-2'><Translate>Ready for export</Translate></th>
                                <th className='col-md-2'><Translate>Exported</Translate></th>
                            </tr>
                            {formData.itemReferences.map((itemReference, index) => {
                                return (
                                    <tr>
                                        <td><Form.Label>{itemReference.externalSystemName}</Form.Label></td>
                                        <td><TextboxControl {...getControlProps(`itemReferences.${index}.referenceCode`)} key={index} /></td>
                                        <td><DateControl {...getControlProps(`itemReferences.${index}.readyForExportOn`)} disabled key={index} /></td>
                                        <td><DateControl {...getControlProps(`itemReferences.${index}.exportedOn`)} disabled key={index} /></td>
                                    </tr>
                                )
                            })
                            }
                        </table>
                    </>
                )}

            </Popup.Body>
            <Popup.Footer>
                <SaveButton {...getSubmitButtonProps()} />
            </Popup.Footer>
        </Popup>
    );
}
export default ChangeItemNrPopup;